import { addDays } from 'date-fns'

// UTILITIES
import { defaultFeePercent, emptyDate } from '@constants'
import { clamp, toPennies } from './number-helper'


// ===================================================================== //
// This file is meant as a temporary storage place for utility functions //
// that don't have proper place or shouldn't be used long term.          //
//                                                                       //
// Everything in this file will eventually be sorted, removed,           //
// or changed at some point, and this file itself will be deleted.       //
// ===================================================================== //


type CSSVariable = string | number
/**
 * @description The `React.CSSProperties` type doesn't support custom properties out of the box.
 * This makes it a bit easier, and typesafe, when changing css properties through typescript.
 * 
 * @param vars An object who's values are prefixed with `--` (which denotes them as properties in css)
 * @param obj The variables obj is cast into this object. Values in the `vars` object overwrite values here.
 * @returns 
 */
export const injectVars = (vars: Record<`--${string}`, (null | undefined | CSSVariable)>, obj: React.CSSProperties = {}): React.CSSProperties => ({
    ...obj, ...Object.keys(vars).reduce<React.CSSProperties>((prev, key) => (
        !!vars[key as any] ? { ...prev, [key]: vars[key as any] } : prev
    ), {})
})

/**
 * @description Hard checks the current .env to make sure the request variable exists before returning it's value.
 * 
 * @param key Can be any key defined in the `vite-env.d.ts` file.
 * @throws If the provided key doesn't exist in the current .env
 * @returns 
 */
export const validateEnvVar = <Key extends keyof ImportMetaEnv>(key: Key): ImportMetaEnv[Key] => {
    const val = import.meta.env[key]
    if (!val) throw new Error(`${key} was not provided as an env var`)
    return val
}


const defaultActivePeriod = 7 // In Days
/**
 * @description Converts group data into a single value in order to determine what stage a group is at in it's lifetime.
 * 
 * @param group 
 * @returns 
 */
export const getGroupStatus = (group: Group) => {
    let ledgerTotal = 0

    if (!!group.ledger) {
        ledgerTotal = group.ledger.reduce(
            (transactionSum, transaction) => transactionSum + transaction.breakdown.reduce((sum, item) => sum + item.amount, 0), 0
        )
    }

    const isLocked = group.closedAt !== emptyDate
    const startDate = new Date(group.closedAt || 0).getTime()
    const endDate = addDays(startDate, defaultActivePeriod).getTime()
    let groupStatus: 'active' | 'completed' | 'canceled' | 'finalized' | 'pending' = 'active'

    if (group.finalizedAt !== emptyDate) groupStatus = 'finalized'
    else if (group.completedAt !== emptyDate && (ledgerTotal !== group.spendAmount)) groupStatus = 'pending'
    else if (group.completedAt !== emptyDate && (ledgerTotal === group.spendAmount)) groupStatus = 'completed'
    else if (group.canceledAt !== emptyDate) groupStatus = 'canceled'

    return { groupStatus, isLocked, startDate, endDate }
}


const feeMin = 50
/**
 * @description Converts the provided value into a number, calculates an amount using the `defaultFeePercent` percentage, 
 * and ensures that amount is greater than a set minimum. This function is primarily meant for calculating Pledge fees.
 * 
 * @param value Either a Number-like string or a number with a percision of 2.
 * @returns Number in Pennies
 */
export const calcPledgeFee = (value: string | number = 0): number => {
    const amount = Math.round(toPennies(value) * defaultFeePercent)
    return clamp(feeMin, amount)
}

export const toggleBodyScroll = (locked: boolean) => {
    const root = document.getElementById('root')
    if (locked) {
        document.documentElement.style.overflow = 'hidden'
        document.body.style.overflow = 'hidden'
        if (root) root.style.overflow = 'hidden'
    } else {
        document.documentElement.style.overflow = ''
        document.body.style.overflow = ''
        if (root) root.style.overflow = ''
    }
}
